import {
  toWSMoneyString,
  WSCircle,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSPage,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { Redirect, Route, useHistory } from "react-router-dom";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { useBankingAccount } from "../../query/bookkeeping/queries";
import { useUserId } from "../../query/hooks/helpers";
import {
  useInstantPayoutQuery,
  useInvoicesQuery,
  usePayoutSettings
} from "../../query/payments/queries";
import { getInvoices } from "../../query/payments/selectors";
import { useAccounts } from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import { InvoicesTable } from "../components/InvoicesTable/InvoicesTable";
import { calculateInvoicesTotalMemberGross } from "../utils";
import { getPayoutSettingsCalculations } from "../utils/payout";
import { InstantPayoutsModal } from "./InstantPayoutsModal";
import { InvoicesInvoiceDetails } from "./InvoicesInvoiceDetails";
import { InvoicesInvoiceTemplateDetails } from "./InvoicesInvoiceTemplateDetails";

export const InvoicesListPaid = () => {
  const history = useHistory();
  const invoicesQuery = useInvoicesQuery();
  const userId = useUserId();
  const payoutSettingsQuery = usePayoutSettings(userId);
  const accountsQuery = useAccounts();
  const instantPayoutQuery = useInstantPayoutQuery();
  const bankingAccountQuery = useBankingAccount();

  return (
    <>
      <Route
        path="/member/invoices/:listName/template/:invoiceTemplateId"
        component={InvoicesInvoiceTemplateDetails}
        exact
      />

      <Route
        path="/member/invoices/paid/:invoiceId/instant-deposit"
        component={InstantPayoutsModal}
        exact
      />

      <Route
        path="/member/invoices/:listName/:invoiceId"
        component={InvoicesInvoiceDetails}
        exact
      />

      <BrowserPageTitle title="Invoices" />

      <WSQueries
        queries={{
          invoicesQuery,
          payoutSettingsQuery,
          accountsQuery
        }}
      >
        {({
          invoicesQuery,
          payoutSettingsQuery: { data: payoutSettings },
          accountsQuery: { data: accounts }
        }) => {
          const {
            isPayoutMethodStandard,
            payoutPercentage,
            standardPayoutAccountName,
            instantPayoutAccountName,
            walletAccountName
          } = getPayoutSettingsCalculations({
            payoutSettings,
            accounts,
            instantPayoutAccount: instantPayoutQuery.data,
            bankingAccount: bankingAccountQuery.data
          });

          const depositText = isPayoutMethodStandard
            ? payoutPercentage.standard.account === 100
              ? standardPayoutAccountName
              : payoutPercentage.standard.wallet === 100
              ? walletAccountName
              : `${standardPayoutAccountName} and ${walletAccountName}`
            : payoutPercentage.instant.account === 100
            ? instantPayoutAccountName
            : payoutPercentage.instant.wallet === 100
            ? walletAccountName
            : `${instantPayoutAccountName} and ${walletAccountName}`;

          const paidInvoices = getInvoices(invoicesQuery.data).filter(
            invoice =>
              invoice.status === InvoiceStatus.Paid ||
              invoice.status === InvoiceStatus.PaymentInTransit
          );

          const pendingInvoices = paidInvoices.filter(
            invoice =>
              (invoice.status === InvoiceStatus.Paid &&
                !!invoice.amountDetails &&
                !invoice.events.depositedAt &&
                !invoice.events.instantPayoutAt) ||
              invoice.status === InvoiceStatus.PaymentInTransit
          );

          const depositedInvoices = paidInvoices.filter(
            invoice =>
              !!invoice.events.depositedAt || !!invoice.events.instantPayoutAt
          );

          const markedAsPaidInvoices = paidInvoices.filter(
            invoice =>
              invoice.status === InvoiceStatus.Paid && !invoice.amountDetails
          );

          return paidInvoices.length > 0 ? (
            <WSPage
              title="Deposits"
              breadcrumb={{
                label: "Invoicing",
                icon: "back",
                onClick: () => {
                  history.push("/member/invoices");
                }
              }}
            >
              <WSText mb="XL">
                Payments will be deposited to {depositText}
              </WSText>
              <WSGrid.Container>
                {pendingInvoices.length > 0 && (
                  <WSGrid.Item S={6}>
                    <WSPanel>
                      <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
                        <WSCircle.Icon
                          size="L"
                          icon="successMark"
                          color="grey300"
                          secondaryColor="green500"
                          mr="M"
                        />
                        <WSElement style={{ flex: 1 }}>
                          <WSText.Heading3 mb="XS">
                            +
                            {toWSMoneyString(
                              calculateInvoicesTotalMemberGross(pendingInvoices)
                            )}
                          </WSText.Heading3>
                          <WSText color="grey700">pending</WSText>
                        </WSElement>
                      </WSFlexBox.CenterY>
                    </WSPanel>
                  </WSGrid.Item>
                )}
              </WSGrid.Container>

              <InvoicesTable
                title="Pending"
                invoices={pendingInvoices}
                my="XL"
                listName="paid"
                sort={(a, b) => {
                  const aPaidDate =
                    a.data.invoice?.events.paidAt ||
                    a.data.invoice?.events.paymentInTransitAt ||
                    new Date();
                  const bPaidDate =
                    b.data.invoice?.events.paidAt ||
                    b.data.invoice?.events.paymentInTransitAt ||
                    new Date();

                  return aPaidDate < bPaidDate ? 1 : -1;
                }}
                pageLimit={10}
              />
              <InvoicesTable
                title="Deposited"
                invoices={[...depositedInvoices, ...markedAsPaidInvoices]}
                listName="paid"
                sort={(a, b) => {
                  const aPaidDate =
                    a.data.invoice?.events.paidAt ||
                    a.data.invoice?.events.paymentInTransitAt ||
                    new Date();
                  const bPaidDate =
                    b.data.invoice?.events.paidAt ||
                    b.data.invoice?.events.paymentInTransitAt ||
                    new Date();

                  return aPaidDate < bPaidDate ? 1 : -1;
                }}
                pageLimit={20}
              />
            </WSPage>
          ) : (
            <Redirect to="/member/invoices" />
          );
        }}
      </WSQueries>
    </>
  );
};
